import { useAppStore } from '@/lib/store';
import { translations } from '@/i18n/translations';
import type { BilingualText, Language } from '@/types';

export function useTranslation() {
  const language = useAppStore((s) => s.language);
  const t = translations[language];

  /** Get localized string from a BilingualText object */
  function localize(text: BilingualText | undefined | null): string {
    if (!text) return '';
    return text[language] || text.en || '';
  }

  return { t, language, localize };
}

export function useCurrency() {
  const currency = useAppStore((s) => s.currency);
  const exchangeRate = useAppStore((s) => s.exchangeRate);

  /** Convert stored price (in cents) + source currency → USD cents baseline */
  function toUsdCents(amountCents: number, sourceCurrency?: 'USD' | 'GEL'): number {
    if (sourceCurrency === 'GEL' && exchangeRate > 0) {
      return Math.round(amountCents / exchangeRate);
    }
    return amountCents;
  }

  /** Format a price in cents to display string, respecting stored currency.
   *  When source matches display currency, skip double-conversion (prevents rounding drift like 3.00 → 3.01). */
  function formatPrice(amountCents: number, sourceCurrency?: 'USD' | 'GEL'): string {
    if (sourceCurrency === currency) {
      const amount = amountCents / 100;
      if (currency === 'GEL') return `${amount.toLocaleString('ka-GE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₾`;
      return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    const usdCents = toUsdCents(amountCents, sourceCurrency);
    if (currency === 'GEL') {
      const amount = (usdCents / 100) * exchangeRate;
      return `${amount.toLocaleString('ka-GE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₾`;
    }
    const amount = usdCents / 100;
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  /** Get both GEL and USD formatted strings for dual display.
   *  Native side (matching source currency) skips conversion to preserve exact amount. */
  function formatDual(amountCents: number, sourceCurrency?: 'USD' | 'GEL'): { primary: string; secondary: string } {
    let gelAmount: number;
    let usdAmount: number;
    if (sourceCurrency === 'GEL') {
      gelAmount = amountCents / 100;
      usdAmount = exchangeRate > 0 ? Math.round(amountCents / exchangeRate) / 100 : 0;
    } else if (sourceCurrency === 'USD') {
      usdAmount = amountCents / 100;
      gelAmount = (amountCents / 100) * exchangeRate;
    } else {
      const usdCents = toUsdCents(amountCents, sourceCurrency);
      usdAmount = usdCents / 100;
      gelAmount = (usdCents / 100) * exchangeRate;
    }
    const gelStr = `${gelAmount.toLocaleString('ka-GE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₾`;
    const usdStr = `$${usdAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (currency === 'GEL') return { primary: gelStr, secondary: usdStr };
    return { primary: usdStr, secondary: gelStr };
  }

  return { currency, formatPrice, formatDual, exchangeRate };
}
